import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Instagram, Youtube, Twitter, Globe, Music2, Disc3, HeartHandshake, Share2, UserPlus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, mediaUrl, API_BASE } from "@/lib/api";
import { fmtNum } from "@/lib/hubs";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { TrackRow } from "@/components/TrackItems";
import { ArtistBadge } from "@/components/ArtistBadge";
import { TipDialog } from "@/components/TipDialog";
import { PlayButton } from "@/components/TrackItems";
import { CollectionCard } from "@/components/Collections";

const SOCIAL = [["tiktok", Music2, "TikTok"], ["instagram", Instagram, "Instagram"], ["youtube", Youtube, "YouTube"], ["x", Twitter, "X"], ["spotify", Disc3, "Spotify"], ["website", Globe, "Website"]];

export default function ArtistPage() {
  const { slug } = useParams();
  const t = useT();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [tip, setTip] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/artists/${slug}`).then((r) => {
      setD(r.data);
      api.get(`/artists/${r.data.artist.user_id}/albums`).then((x) => setAlbums(x.data));
      document.title = `${r.data.artist.name} | Soundra`;
    }).catch(() => setErr("Artist not found"));
    sessionStorage.setItem("sonora_src", "artist_profile");
  }, [slug]);
  if (err) return <div className="py-24 text-center text-zinc-400" data-testid="artist-not-found">{err}</div>;
  if (!d) return <div className="h-80 rounded-3xl bg-white/5 animate-pulse" />;
  const a = d.artist;
  const follow = async () => {
    if (!user) return toast.error("Sign in to follow artists");
    try {
      const { data } = await api.post(`/artists/${a.user_id}/follow`);
      setD({ ...d, is_following: data.following, artist: { ...a, followers_count: data.followers_count } });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const share = () => { navigator.clipboard?.writeText(`${API_BASE}/api/share/artist/${a.slug}`); toast.success("Share link copied (with rich preview)"); };
  const isMe = user && user.user_id === a.user_id;
  return (
    <div data-testid="artist-profile-page" className="-mt-8">
      <div className="relative h-64 sm:h-80 -mx-4 sm:-mx-6 lg:-mx-10 overflow-hidden">
        {a.cover ? <img src={mediaUrl(a.cover)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-[var(--hub)] to-black" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F] via-[#0A0A0F]/40 to-transparent" />
      </div>
      <div className="relative -mt-24 flex flex-col md:flex-row md:items-end gap-6 reveal">
        <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-full overflow-hidden ring-4 ring-[#0A0A0F] bg-zinc-800 shrink-0">
          {a.avatar && <img src={mediaUrl(a.avatar)} alt={a.name} className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="eyebrow">Artist · {a.country}</div>
          <h1 data-testid="artist-name" className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight flex items-center gap-3">{a.name} <ArtistBadge badge={a.badge} size={30} /></h1>
          <div className="flex gap-2 mt-3">
            {SOCIAL.filter(([k]) => a.socials?.[k]).map(([k, Icon, label]) => (
              <a key={k} href={a.socials[k]} target="_blank" rel="noreferrer" data-testid={`social-link-${k}`} aria-label={label} className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-zinc-300 hover:text-white hover:border-white/40 transition-colors"><Icon size={15} /></a>
            ))}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isMe && (
            <button data-testid="artist-follow-button" onClick={follow} className={`inline-flex items-center gap-2 px-5 h-11 rounded-full font-semibold transition-colors ${d.is_following ? "border border-white/20" : "bg-white text-black hover:bg-zinc-200"}`}>
              {d.is_following ? <><UserCheck size={16} /> {t("following")}</> : <><UserPlus size={16} /> {t("follow")}</>}
            </button>
          )}
          <button data-testid="artist-profile-tip-button" onClick={() => setTip(true)} className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[var(--hub)] text-white font-semibold hover:brightness-110 transition-[filter]"><HeartHandshake size={16} /> {t("tip")}</button>
          <button data-testid="artist-share-button" onClick={share} className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center hover:border-white/40" aria-label="Share"><Share2 size={16} /></button>
        </div>
      </div>
      <div className="grid md:grid-cols-[1fr_2fr] gap-10 mt-10">
        <aside className="space-y-6">
          <div className="grid grid-cols-3 surface divide-x divide-white/5" data-testid="artist-public-metrics">
            {[["streams", a.total_streams, "metric-streams"], ["tracks", a.track_count, "metric-tracks"], ["followers", a.followers_count, "metric-followers"]].map(([k, v, id]) => (
              <div key={k} className="p-4 text-center"><div data-testid={id} className="font-display text-2xl sm:text-3xl font-black">{fmtNum(v)}</div><div className="eyebrow mt-1">{t(k)}</div></div>
            ))}
          </div>
          <div className="surface p-5"><div className="eyebrow mb-2">Bio</div><p className="text-zinc-300 leading-relaxed whitespace-pre-line" data-testid="artist-bio">{a.bio || "No bio yet."}</p></div>
        </aside>
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl sm:text-3xl font-bold">{t("tracks")}</h2>
            {d.tracks[0] && <PlayButton track={d.tracks[0]} list={d.tracks} size={48} testId="artist-play-all" />}
          </div>
          {albums.length > 0 && (
            <div className="mb-8" data-testid="artist-albums">
              <div className="eyebrow mb-3">Albums & EPs</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">{albums.map((al) => <CollectionCard key={al.album_id} item={al} kind="album" />)}</div>
            </div>
          )}
          <div className="surface p-2" data-testid="artist-tracklist">
            {d.tracks.length === 0 && <div className="p-6 text-zinc-500">No public tracks yet.</div>}
            {d.tracks.map((tr) => <TrackRow key={tr.track_id} track={tr} list={d.tracks} />)}
          </div>
        </section>
      </div>
      <TipDialog open={tip} onOpenChange={setTip} artist={a} />
    </div>
  );
}
